import React, { useEffect, useState } from 'react';
import { Modal, Pressable, StyleSheet, Text, View } from 'react-native';
import { useAppTheme } from '@/theme';

/**
 * The app's one alert system: a themed in-app dialog (same look as ConfirmDialog) instead of the
 * plain system Alert. Call it exactly like Alert.alert:
 *
 *   showAlert('Delete coupon?', 'Customers can no longer use it.', [
 *     { text: 'Cancel', style: 'cancel' },
 *     { text: 'Delete', style: 'destructive', onPress: remove },
 *   ]);
 *
 * Buttons: 'cancel' is the outlined one, 'destructive' is red, anything else is the purple
 * primary. With no buttons it shows a single OK. <AlertHost /> (mounted once in the root layout)
 * draws whichever alert is showing; alerts raised while one is open wait their turn.
 */
export interface AlertButton {
  text?: string;
  style?: 'default' | 'cancel' | 'destructive';
  onPress?: () => unknown;
}
interface AlertRequest {
  id: number;
  title: string;
  message?: string;
  buttons: AlertButton[];
  cancelable: boolean;
}

let queue: AlertRequest[] = [];
let nextId = 1;
const listeners = new Set<(q: AlertRequest[]) => void>();
const emit = () => listeners.forEach((l) => l(queue));

export function showAlert(title: string, message?: string, buttons?: AlertButton[], options?: { cancelable?: boolean }) {
  const list = buttons && buttons.length ? buttons : [{ text: 'OK' }];
  queue = [...queue, { id: nextId++, title, message, buttons: list, cancelable: options?.cancelable ?? true }];
  emit();
}

function dismiss(id: number) {
  queue = queue.filter((a) => a.id !== id);
  emit();
}

export function AlertHost() {
  const { colors } = useAppTheme();
  const [items, setItems] = useState<AlertRequest[]>(queue);
  useEffect(() => {
    listeners.add(setItems);
    return () => {
      listeners.delete(setItems);
    };
  }, []);

  const current = items[0];
  if (!current) return null;

  const press = (b?: AlertButton) => {
    dismiss(current.id);
    if (b?.onPress) void b.onPress();
  };
  const cancelButton = current.buttons.find((b) => b.style === 'cancel');
  const stacked = current.buttons.length > 2;
  // The cancel button sits first (left, or top when stacked), as in ConfirmDialog.
  const ordered = [...current.buttons].sort((a, b) => Number(b.style === 'cancel') - Number(a.style === 'cancel'));

  return (
    <Modal visible transparent animationType="fade" onRequestClose={() => current.cancelable && press(cancelButton)}>
      <Pressable style={[styles.overlay, { backgroundColor: colors.overlay }]} onPress={() => current.cancelable && press(cancelButton)}>
        <Pressable style={[styles.card, { backgroundColor: colors.surface, borderColor: colors.border }]} onPress={() => undefined}>
          <Text style={[styles.title, { color: colors.textPrimary }]}>{current.title}</Text>
          {current.message ? <Text style={[styles.message, { color: colors.textSecondary }]}>{current.message}</Text> : null}
          <View style={[styles.actions, stacked && styles.actionsStacked]}>
            {ordered.map((b, i) => {
              const isCancel = b.style === 'cancel';
              const isDanger = b.style === 'destructive';
              return (
                <Pressable
                  key={`${b.text}-${i}`}
                  accessibilityRole="button"
                  onPress={() => press(b)}
                  style={({ pressed }) => [
                    styles.btn,
                    !stacked && styles.btnFlex,
                    isCancel
                      ? { backgroundColor: 'transparent', borderColor: colors.border, borderWidth: 1 }
                      : { backgroundColor: isDanger ? colors.error : colors.accent },
                    pressed && styles.pressed,
                  ]}>
                  <Text style={[styles.btnText, { color: isCancel ? colors.textPrimary : '#FFFFFF' }]} numberOfLines={2}>
                    {b.text ?? 'OK'}
                  </Text>
                </Pressable>
              );
            })}
          </View>
        </Pressable>
      </Pressable>
    </Modal>
  );
}

const styles = StyleSheet.create({
  overlay: { flex: 1, alignItems: 'center', justifyContent: 'center', padding: 24 },
  card: {
    width: '100%',
    maxWidth: 400,
    borderRadius: 18,
    borderWidth: 1,
    padding: 20,
    gap: 8,
    shadowColor: '#000',
    shadowOpacity: 0.2,
    shadowRadius: 18,
    shadowOffset: { width: 0, height: 8 },
    elevation: 12,
  },
  title: { fontSize: 18, fontWeight: '800' },
  message: { fontSize: 14, lineHeight: 20, marginBottom: 4 },
  actions: { flexDirection: 'row', gap: 10, marginTop: 8 },
  actionsStacked: { flexDirection: 'column' },
  btn: { minHeight: 46, borderRadius: 12, alignItems: 'center', justifyContent: 'center', paddingHorizontal: 12 },
  btnFlex: { flex: 1 },
  btnText: { fontSize: 15, fontWeight: '700', textAlign: 'center' },
  pressed: { opacity: 0.85 },
});
